import React, { useState, useRef } from 'react'
import { Stack, Typography } from '@sureco/design-system'
import { Button, message, Card } from '@surecompanies/core_components'
import { UploadOutlined, FileTextOutlined } from '@ant-design/icons'
import usePaymentBulkUpload from 'modules/finance/payment-bulk/usePaymentBulkUpload'

export default function PaymentBulkUpload() {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { handleUpload, isUploading } = usePaymentBulkUpload()

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      if (!file.name.endsWith('.csv') && !file.name.endsWith('.xlsx')) {
        message.error('Please select a CSV or XLSX file')
        return
      }
      setSelectedFile(file)
    }
  }

  const handleUploadClick = () => {
    if (selectedFile) {
      handleUpload(selectedFile)
    }
  }

  const handleReset = () => {
    setSelectedFile(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  return (
    <Card style={{ padding: '24px' }}>
      <Stack space={16}>
        <Typography size={18} bold>
          Payment Bulk Transaction File Upload
        </Typography>
        <Typography size={14} color="textSecondary">
          Upload a CSV or XLSX file to process payment bulk transactions. The
          file should contain the required columns for payment processing.
        </Typography>

        <Stack space={12}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />

          <Button
            type="default"
            icon={<UploadOutlined />}
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
          >
            Select File (CSV/XLSX)
          </Button>

          {selectedFile && (
            <Stack direction="row" space={8} align="center">
              <FileTextOutlined style={{ color: '#1890ff' }} />
              <Typography size={14}>
                {selectedFile.name} ({formatFileSize(selectedFile.size)})
              </Typography>
            </Stack>
          )}

          {selectedFile && (
            <Stack direction="row" space={8}>
              <Button
                type="primary"
                loading={isUploading}
                onClick={handleUploadClick}
              >
                Upload File
              </Button>
              <Button onClick={handleReset} disabled={isUploading}>
                Reset
              </Button>
            </Stack>
          )}
        </Stack>
      </Stack>
    </Card>
  )
}
